import { EntityTypes } from '@generated/graphql';

export type FormPdfBytes = {
  name: string;
  bytes: Uint8Array;
};

export type FormsZipUserName = {
  type?: EntityTypes | null;
  firstName?: string | null;
  lastName?: string | null;
  entityName?: string | null;
  householdName?: string | null;
};

const zipNamePart = (value: string) => value.trim().replace(/\s+/g, '_');

export const defaultFormsZipFileName = (user: FormsZipUserName, at: Date) => {
  const stamp = at.toISOString();
  if (typeof user.householdName === 'string') {
    return `${zipNamePart(user.householdName)}-${stamp}.zip`;
  }
  if (user.type && user.type !== EntityTypes.Individual) {
    return `${zipNamePart(user.entityName ?? '')}-${stamp}.zip`;
  }
  return `${zipNamePart(user.firstName ?? '')}-${zipNamePart(user.lastName ?? '')}-${stamp}.zip`;
};

const PDF_EXTENSION = '.pdf';

const sanitizeZipEntryName = (name: string) => {
  const trimmed = name.replace(/[\\/]/g, ' ').replace(/\s+/g, ' ').trim();
  const base = trimmed || 'form';
  return base.toLowerCase().endsWith(PDF_EXTENSION)
    ? base
    : `${base}${PDF_EXTENSION}`;
};

export const uniquePdfZipEntryName = (name: string, used: Set<string>) => {
  const fileName = sanitizeZipEntryName(name);
  const key = fileName.toLowerCase();
  if (!used.has(key)) {
    used.add(key);
    return fileName;
  }

  const stem = fileName.slice(0, -PDF_EXTENSION.length);
  let index = 2;
  let next = `${stem} (${index})${PDF_EXTENSION}`;
  while (used.has(next.toLowerCase())) {
    index += 1;
    next = `${stem} (${index})${PDF_EXTENSION}`;
  }
  used.add(next.toLowerCase());
  return next;
};

// Local time, packed the way zip entries store it, so it matches the zip file in Finder.
const dosDateTime = (at: Date) => ({
  date:
    ((at.getFullYear() - 1980) << 9) |
    ((at.getMonth() + 1) << 5) |
    at.getDate(),
  time: (at.getHours() << 11) | (at.getMinutes() << 5) | (at.getSeconds() >> 1),
});

const CRC_TABLE = new Uint32Array(256).map((_, index) => {
  let crc = index;
  for (let bit = 0; bit < 8; bit += 1) {
    crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  }
  return crc;
});

const crc32 = (bytes: Uint8Array) => {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const concatParts = (parts: Uint8Array[]) => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const zip = new Uint8Array(total);
  let cursor = 0;
  for (const part of parts) {
    zip.set(part, cursor);
    cursor += part.length;
  }
  return zip;
};

export const zipFormPdfs = (pdfs: FormPdfBytes[], at = new Date()) => {
  const modified = dosDateTime(at);
  const used = new Set<string>();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const encoder = new TextEncoder();

  for (const pdf of pdfs) {
    const nameBytes = encoder.encode(uniquePdfZipEntryName(pdf.name, used));
    const checksum = crc32(pdf.bytes);
    const local = new Uint8Array(30 + nameBytes.length + pdf.bytes.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0x0800, true);
    localView.setUint16(10, modified.time, true);
    localView.setUint16(12, modified.date, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, pdf.bytes.length, true);
    localView.setUint32(22, pdf.bytes.length, true);
    localView.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);
    local.set(pdf.bytes, 30 + nameBytes.length);
    parts.push(local);

    const header = new Uint8Array(46 + nameBytes.length);
    const headerView = new DataView(header.buffer);
    headerView.setUint32(0, 0x02014b50, true);
    headerView.setUint16(4, 20, true);
    headerView.setUint16(6, 20, true);
    headerView.setUint16(8, 0x0800, true);
    headerView.setUint16(12, modified.time, true);
    headerView.setUint16(14, modified.date, true);
    headerView.setUint32(16, checksum, true);
    headerView.setUint32(20, pdf.bytes.length, true);
    headerView.setUint32(24, pdf.bytes.length, true);
    headerView.setUint16(28, nameBytes.length, true);
    headerView.setUint32(42, offset, true);
    header.set(nameBytes, 46);
    central.push(header);
    offset += local.length;
  }

  const centralSize = central.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, pdfs.length, true);
  endView.setUint16(10, pdfs.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);

  return concatParts([...parts, ...central, end]);
};

export const downloadZipFile = (bytes: Uint8Array, fileName: string) => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const url = URL.createObjectURL(
    new Blob([buffer], { type: 'application/zip' }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking in the same turn cancels the download in some browsers
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
